import React, { createContext, useContext, useState, useEffect } from 'react';
import { userAPI } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    // Support both 'google_token' (for OAuth) and 'auth_token' (for local/GitHub auth)
    const token = localStorage.getItem('google_token') || localStorage.getItem('auth_token');
    if (token) {
      // Verify token is still valid by calling /api/ft/me
      userAPI.me()
        .then((response) => {
          setUser(response.data);
          setIsAuthenticated(true);
        })
        .catch(() => {
          // Token is invalid, clear it
          localStorage.removeItem('google_token');
          localStorage.removeItem('auth_token');
          setIsAuthenticated(false);
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData = null) => {
    // Always store token in 'google_token' for API interceptor compatibility
    // The API interceptor looks for 'google_token' in localStorage
    localStorage.setItem('google_token', token);
    setIsAuthenticated(true);
    
    if (userData) {
      // Store user data immediately
      setUser(userData);
      // Also fetch full user data from /api/ft/me to get complete user info
      userAPI.me()
        .then((response) => {
          // Merge the response data with the login userData
          setUser({
            ...userData,
            ...response.data,
            user: response.data.user || userData.user,
          });
        })
        .catch((err) => {
          console.error('Failed to fetch user data:', err);
          // Keep the userData from login if /api/ft/me fails
        });
    } else {
      // Fetch user data
      userAPI.me()
        .then((response) => {
          setUser(response.data);
        })
        .catch((err) => {
          console.error('Failed to fetch user data:', err);
        });
    }
  };

  const logout = () => {
    localStorage.removeItem('google_token');
    localStorage.removeItem('auth_token');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

